import { useEffect, useMemo, useState } from "react";
import type { EventItem } from "@/types";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/categories";
import { EventRow } from "@/components/base/EventCard";
import { LoadingState, EmptyState, ErrorState } from "@/components/base/StateViews";

type SortKey = "latest" | "earliest" | "views";

const PAGE_SIZE = 4;

export default function EventExplorer({
  initialCategory = "all",
  query = "",
}: {
  initialCategory?: string;
  query?: string;
}) {
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setCategoryFilter(initialCategory);
    setDateFrom("");
    setDateTo("");
    setSort("latest");
    setVisible(PAGE_SIZE);
  }, [initialCategory, query]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      setLoading(false);
      setVisible(PAGE_SIZE);
    }, 500);
    return () => clearTimeout(timer);
  }, [categoryFilter, dateFrom, dateTo, sort, query]);

  const filtered = useMemo(() => {
    let list = [...events] as EventItem[];
    if (categoryFilter !== "all") list = list.filter((e) => e.categoryId === categoryFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((e) =>
        [e.title, e.summary, e.location, e.highlight, ...e.tags, ...e.teams.map((t) => t.name)]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    if (dateFrom) list = list.filter((e) => e.date >= dateFrom);
    if (dateTo) list = list.filter((e) => e.date <= dateTo);
    list.sort((a, b) => {
      if (sort === "latest") return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
      if (sort === "earliest") return a.date > b.date ? 1 : a.date < b.date ? -1 : 0;
      return b.views - a.views;
    });
    return list;
  }, [categoryFilter, query, dateFrom, dateTo, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  function resetFilters() {
    setCategoryFilter(query ? "all" : initialCategory);
    setDateFrom("");
    setDateTo("");
    setSort("latest");
    setVisible(PAGE_SIZE);
  }

  const filterPanel = (
    <div className="flex flex-col gap-4">
      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
          赛事子分类
        </label>
        <div className="flex flex-col gap-1">
          <FilterButton
            active={categoryFilter === "all"}
            onClick={() => setCategoryFilter("all")}
            label="全部类别"
            icon="ri-apps-2-line"
          />
          {categories.map((c) => (
            <FilterButton
              key={c.id}
              active={categoryFilter === c.id}
              onClick={() => setCategoryFilter(c.id)}
              label={c.name}
              icon={c.icon}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
          日期范围
        </label>
        <div className="flex flex-col gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
          排序方式
        </label>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
        >
          <option value="latest">时间：由新到旧</option>
          <option value="earliest">时间：由旧到新</option>
          <option value="views">热度：阅读量优先</option>
        </select>
      </div>

      <button
        type="button"
        onClick={resetFilters}
        className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
      >
        <i className="ri-refresh-line"></i>
        重置筛选条件
      </button>

      <p className="rounded-md bg-secondary-50 px-3 py-2 text-xs leading-relaxed text-secondary-800">
        <i className="ri-information-line"></i> 当前为演示版筛选，结果由前端示例数据计算，真实数据后续接入。
      </p>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block">
        <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-4">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-foreground-900">
            <i className="ri-filter-3-line text-primary-600"></i>
            筛选条件
          </h2>
          {filterPanel}
        </div>
      </aside>

      <div className="min-w-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-bold text-foreground-950">{filtered.length}</span> 条赛事资讯
          </p>
          <button
            type="button"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm font-semibold text-foreground-700 lg:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>

        {mobileFilterOpen && (
          <div className="mb-4 rounded-lg border border-background-200 bg-background-50 p-4 lg:hidden">
            {filterPanel}
          </div>
        )}

        {loading ? (
          <LoadingState label="正在筛选赛事资讯…" />
        ) : error ? (
          <ErrorState
            onAction={() => {
              setError(false);
              setLoading(true);
              setTimeout(() => setLoading(false), 500);
            }}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            title="没有匹配的赛事资讯"
            desc="当前筛选条件或搜索关键词没有匹配结果，试试重置筛选，或返回首页浏览全部内容。"
            actionLabel="重置筛选"
            onAction={resetFilters}
            icon="ri-search-eye-line"
          />
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </div>

            {hasMore ? (
              <div className="mt-6 flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多赛事
                </button>
                <p className="text-xs text-foreground-500">
                  已显示 {shown.length} / {filtered.length} 条
                </p>
              </div>
            ) : (
              <p className="mt-6 text-center text-xs text-foreground-500">
                已加载全部 {filtered.length} 条赛事资讯
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
        active
          ? "bg-primary-100 font-semibold text-primary-800"
          : "text-foreground-700 hover:bg-background-100"
      }`}
    >
      <i className={`${icon} text-base`}></i>
      {label}
    </button>
  );
}